import {
  NotebookCellResource,
  NotebookCellRunResource,
  NotebookCellRunResultResource,
  NotebookResource,
  SessionResource,
} from '@mode/legacy/types-hal-resource';
import { NotebookTypes } from '@mode/notebooks/contract';
import { Map, OrderedSet, Set } from 'immutable';

export enum NotebookState {
  Cancelling = 'cancelling',
  Cancelled = 'cancelled',
  Running = 'running',
  Loading = 'loading',
  Warning = 'warning',
  Session = 'session', // loading the session
  Busy = 'busy',
  Error = 'error',
  Ready = 'ready',
  Alert = 'alert', // new data available
}

export enum NotebookVizState {
  Loading = 'loading',
  Empty = 'empty',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Skipped = 'skipped',
}

export enum NotebookRunState {
  Uninitialized = 'uninitialized',
  Loading = 'loading',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Canceled = 'canceled',
  Skipped = 'skipped',
}

export interface NotebookEditorState {
  readonly selectedNotebookToken: string;
  readonly selectedCellToken: string;
  readonly isResultSelected: boolean;
  readonly isEditing: boolean;
}

export interface NormalizedNotebooksArray {
  readonly tokens: OrderedSet<string>;
  readonly pusherChannelNames: Map<string, string>;
  readonly sessions: Map<string, SessionResource>;
  readonly resources: Map<string, NotebookResource>;
  readonly initialized: Map<string, boolean>;
  readonly states: Map<string, NotebookState>;
  readonly awaitingInitialRun: Map<string, boolean>;
  readonly notebookTypes: Map<string, NotebookTypes.NotebookType>;

  readonly cellTokensByNotebook: Map<string, OrderedSet<string>>;
}

export interface NormalizedNotebookCellArray {
  readonly tokens: OrderedSet<string>;
  readonly contents: Map<string, string>;
  readonly frozen: Map<string, boolean>;
  readonly folded: Map<string, boolean>;
  readonly contentTypes: Map<string, NotebookTypes.ContentType>;
  readonly createdAt: Map<string, Date>;
  readonly resources: Map<string, NotebookCellResource>;
  readonly executing: Map<string, boolean>;
  readonly lastRunTokens: Map<string, string>;
  readonly missingRuns: Set<string>;
}

export interface NormalizedNotebookCellRunArray {
  readonly tokens: OrderedSet<string>;
  readonly executionCounts: Map<string, number>;
  readonly resources: Map<string, NotebookCellRunResource>;
  readonly resultTokens: Map<string, OrderedSet<string>>;
  readonly missingResults: Set<string>;
}

export interface NormalizedNotebookCellRunResultArray {
  readonly tokens: OrderedSet<string>;
  readonly resultTypes: Map<string, string>;
  readonly dates: Map<string, string>;
  readonly contentTypes: Map<string, string>;
  readonly contents: Map<string, string>;
  readonly resources: Map<string, NotebookCellRunResultResource>;
}

export interface NormalizedNotebook {
  readonly token: string;
  readonly pusherChannelName: string;
  readonly session: SessionResource;
  readonly resource: NotebookResource;
  readonly initialized: boolean;
  readonly cellTokens: string[];
}

export interface NormalizedNotebookCellExRes {
  readonly token: string;
  readonly content: string;
  readonly content_type: NotebookTypes.ContentType;
  readonly frozen: boolean;
  readonly folded: boolean;
  readonly executing: boolean;
  readonly created_at: Date;
  readonly lastRunToken: string;
  readonly missingRun: boolean;
}

export interface NormalizedNotebookCell extends NormalizedNotebookCellExRes {
  readonly resource: NotebookCellResource;
}

export interface NotebookCellState extends NormalizedNotebookCell {
  readonly isInLayout: boolean;
}

export interface NormalizedNotebookCellRun {
  readonly token: string;
  readonly execution_count: number;
  readonly resource: NotebookCellRunResource;
  readonly resultTokens: string[];
  readonly missingResult: boolean;
}

export interface NormalizedNotebookCellRunResult extends NotebookVizResult {
  readonly resource: NotebookCellRunResultResource;
}

export interface NotebookVizResult {
  token: string; // viz token
  content: string;
  result_type: string;
  date: string;
  content_type: string;
}

// for backwards compatibility with python components that expect a type property
export interface NotebookEmbedVizResult extends NotebookVizResult {
  type: string; // content_type alias
}

export interface NotebookVizRender {
  _name: 'pythonVisualization'; // required by VizContainer
  id: string;
  token: string;
  title: string;
  frozen: boolean;
  state: NotebookVizState;
  vizLink: string;
  vizReportRunLink: string;
  results: NotebookEmbedVizResult[];
}
